import React from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { PUBLIC_PLATFORM_PATHS } from '../../constants';

/**
 * Lands a returning user directly in their organization.
 *
 * Must render inside BOTH TenantProvider and AuthProvider: it needs `memberships`
 * from AuthContext and `switchOrg` from TenantContext. TenantProvider wraps
 * AuthProvider in App.tsx, so this cannot live inside either context directly.
 *
 * Only applies when the user belongs to exactly one org. With several orgs there is
 * no obvious default, so we leave the choice to the OrgSwitcher.
 */
export const DefaultOrgResolver: React.FC = () => {
  const { memberships, isSignedIn, isLoading: isAuthLoading, isSyncFailed, isBackendSynced } = useCurrentUser();
  const { activeOrg, isUniversal, switchOrg } = useTenant();
  const location = useLocation();

  // Resolve at most once per page load. Without this, a user who deliberately clicks
  // "Exit to Platform" (which clears active_org) would be pulled straight back in.
  const hasResolved = React.useRef(false);

  const isPublicPath = React.useMemo(() => {
    const path = location.pathname.toLowerCase();
    const normalized = path.replace(/\/$/, '') || '/';
    return PUBLIC_PLATFORM_PATHS.some(
      p => p === '/' ? normalized === '/' : normalized === p || normalized.startsWith(p + '/')
    );
  }, [location.pathname]);

  React.useEffect(() => {
    if (hasResolved.current) return;
    if (!isSignedIn || isAuthLoading || isSyncFailed) return;
    // Memberships are only trustworthy once the backend sync has finished; deciding
    // earlier would lock in a decision made against an empty list.
    if (!isBackendSynced) return;
    // Public pages don't count: a visitor who starts on the landing page should still be
    // resolved once they reach the app.
    if (isPublicPath || isUniversal) return;

    // Decide exactly once, whatever the outcome. Marking only on a successful switch
    // would let a user who began with a saved org, then exited to the platform, get
    // pulled straight back in by this same effect.
    hasResolved.current = true;

    // Respect an existing selection, whether from storage or an in-session switch.
    if (activeOrg) return;
    if (localStorage.getItem('active_org')) return;
    if (memberships.length !== 1) return;

    const soleSlug = memberships[0]?.nodeSlug;
    if (!soleSlug || soleSlug === 'unknown' || soleSlug === 'universal') return;

    switchOrg(soleSlug, location.pathname);
  }, [isSignedIn, isAuthLoading, isSyncFailed, isBackendSynced, isPublicPath, isUniversal, activeOrg, memberships, switchOrg, location.pathname]);

  return null;
};
